import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { IoSearchOutline, IoCloseCircleOutline } from "react-icons/io5";
import { Link } from "react-router";
import styled from "styled-components";

import SearchBimClasses from "./SearchBimClasses.graphql";
import SearchCategories from "./SearchCategories.graphql";
import SearchGeometryTypes from "./SearchGeometryTypes.graphql";
import SearchParameterGroups from "./SearchParameterGroups.graphql";
import SearchParameters from "./SearchParameters.graphql";
import { TAXONOMY_BASE } from "../../../../constants";

const StyledSearchContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;

    .search-bar {
        align-items: center;
        background: #f7f9fa;
        border: 1px solid #e1e4e8;
        border-radius: 8px;
        display: flex;
        gap: 10px;
        padding: 10px 16px;
        transition: border-color 0.2s, box-shadow 0.2s;

        &:focus-within {
            background: #fff;
            border-color: #0052cc;
            box-shadow: 0 0 0 3px rgba(0, 82, 204, 0.12);
        }

        svg {
            color: #6b778c;
            flex-shrink: 0;
            font-size: 1.25rem;
        }

        input {
            background: transparent;
            border: none;
            color: #172b4d;
            font-size: 0.95rem;
            outline: none;
            width: 100%;

            &::placeholder {
                color: #8993a4;
            }
        }

        .clear-btn {
            background: none;
            border: none;
            color: #8993a4;
            cursor: pointer;
            display: flex;
            padding: 0;

            &:hover {
                color: #172b4d;
            }
        }
    }

    .results-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .results-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .result-item {
        align-items: center;
        background: #fff;
        border: 1px solid #ebecf0;
        border-radius: 6px;
        display: flex;
        justify-content: space-between;
        padding: 10px 14px;
        text-decoration: none;
        transition: background 0.15s, border-color 0.15s, transform 0.15s;

        &:hover {
            background: #f4f5f7;
            border-color: #dfe1e6;
            transform: translateX(2px);
        }

        .item-info {
            align-items: center;
            display: flex;
            gap: 10px;
        }

        .item-name {
            color: #172b4d;
            font-weight: 500;
        }
    }

    .badge {
        border-radius: 4px;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        padding: 2px 8px;
        text-transform: uppercase;

        &.category {
            background: #deebff;
            color: #0747a6;
        }
        &.bimclass {
            background: #e3fcef;
            color: #006644;
        }
        &.geometrytype {
            background: #eae6ff;
            color: #403294;
        }
        &.parametergroup {
            background: #fffae6;
            color: #172b4d;
        }
        &.parameter {
            background: #ffebe6;
            color: #bf2600;
        }
    }

    .no-results {
        color: #6b778c;
        font-size: 0.9rem;
        padding: 12px 4px;
    }
`;

export default function Search() {
    const [searchBimClasses, searchBimClassesResult] = useLazyQuery(gql(SearchBimClasses));
    const [searchCategories, searchCategoriesResult] = useLazyQuery(gql(SearchCategories));
    const [searchGeometryTypes, searchGeometryTypesResult] = useLazyQuery(gql(SearchGeometryTypes));
    const [searchParameterGroups, searchParameterGroupsResult] = useLazyQuery(gql(SearchParameterGroups));
    const [searchParameters, searchParametersResult] = useLazyQuery(gql(SearchParameters));

    const [term, setTerm] = useState("");

    useEffect(
        () => {
            if (term.length < 3) return;

            void searchBimClasses({ variables: { term } });
            void searchCategories({ variables: { term } });
            void searchGeometryTypes({ variables: { term } });
            void searchParameterGroups({ variables: { term } });
            void searchParameters({ variables: { term } });
        },
        [term],
    );

    const hasResults
        = (searchBimClassesResult.data?.searchBimClasses?.length > 0)
        || (searchCategoriesResult.data?.searchCategories?.length > 0)
        || (searchGeometryTypesResult.data?.searchGeometryTypes?.length > 0)
        || (searchParameterGroupsResult.data?.searchParameterGroups?.length > 0)
        || (searchParametersResult.data?.searchParameters?.length > 0);

    const isLoading
        = searchBimClassesResult.loading
        || searchCategoriesResult.loading
        || searchGeometryTypesResult.loading
        || searchParameterGroupsResult.loading
        || searchParametersResult.loading;

    return (
        <StyledSearchContainer>
            <div className="search-bar">
                <IoSearchOutline />
                <input
                    onChange={ (e) => setTerm(e.target.value) }
                    placeholder="Search taxonomy (type at least 3 characters)..."
                    type="text"
                    value={ term }
                />
                { term && (
                    <button className="clear-btn" onClick={ () => setTerm("") }>
                        <IoCloseCircleOutline />
                    </button>
                ) }
            </div>

            { term.length >= 3 && (
                <div className="results-container">
                    { isLoading && <div className="no-results">Searching...</div> }

                    { !isLoading && !hasResults && (
                        <div className="no-results">No matching taxonomy items found.</div>
                    ) }

                    <ul className="results-list">
                        {
                            searchBimClassesResult.data?.searchBimClasses?.toSorted(
                                (a, b) => a.data.name[0].text.localeCompare(b.data.name[0].text)
                            ).map(
                                (result) => (
                                    <li key={ result.id }>
                                        <Link className="result-item" to={ `${ TAXONOMY_BASE }/BimClasses/${ result.id }` }>
                                            <div className="item-info">
                                                <span className="badge bimclass">BimClass</span>
                                                <span className="item-name">{ result.data.name[0].text }</span>
                                            </div>
                                        </Link>
                                    </li>
                                )
                            )
                        }
                        {
                            searchCategoriesResult.data?.searchCategories?.toSorted(
                                (a, b) => a.name[0].text.localeCompare(b.name[0].text)
                            ).map(
                                (result) => (
                                    <li key={ result.id }>
                                        <Link className="result-item" to={ `${ TAXONOMY_BASE }/Categories/${ result.id }` }>
                                            <div className="item-info">
                                                <span className="badge category">Category</span>
                                                <span className="item-name">{ result.name[0].text }</span>
                                            </div>
                                        </Link>
                                    </li>
                                )
                            )
                        }
                        {
                            searchGeometryTypesResult.data?.searchGeometryTypes?.toSorted(
                                (a, b) => a.name[0].text.localeCompare(b.name[0].text)
                            ).map(
                                (result) => (
                                    <li key={ result.id }>
                                        <Link className="result-item" to={ `${ TAXONOMY_BASE }/GeometryTypes/${ result.id }` }>
                                            <div className="item-info">
                                                <span className="badge geometrytype">Geometry Type</span>
                                                <span className="item-name">{ result.name[0].text }</span>
                                            </div>
                                        </Link>
                                    </li>
                                )
                            )
                        }
                        {
                            searchParameterGroupsResult.data?.searchParameterGroups?.toSorted(
                                (a, b) => a.name[0].text.localeCompare(b.name[0].text)
                            ).map(
                                (result) => (
                                    <li key={ result.id }>
                                        <Link className="result-item" to={ `${ TAXONOMY_BASE }/ParameterGroups/${ result.id }` }>
                                            <div className="item-info">
                                                <span className="badge parametergroup">Parameter Group</span>
                                                <span className="item-name">{ result.name[0].text }</span>
                                            </div>
                                        </Link>
                                    </li>
                                )
                            )
                        }
                        {
                            searchParametersResult.data?.searchParameters?.toSorted(
                                (a, b) => a.name[0].text.localeCompare(b.name[0].text)
                            ).map(
                                (result) => (
                                    <li key={ result.id }>
                                        <Link className="result-item" to={ `${ TAXONOMY_BASE }/Parameters/${ result.id }` }>
                                            <div className="item-info">
                                                <span className="badge parameter">Parameter</span>
                                                <span className="item-name">{ result.name[0].text }</span>
                                            </div>
                                        </Link>
                                    </li>
                                )
                            )
                        }
                    </ul>
                </div>
            ) }
        </StyledSearchContainer>
    );
}
